import { featuresThatMakesKlapBetter } from "../../app/(marketing)/_lib/landing-data";
import type { BenefitBlockData } from "../../components/blocks/BenefitBlock";
import type { FeaturesBlockData } from "../../components/blocks/FeaturesBlock";
import type { HowToBlockData } from "../../components/blocks/HowToBlock";
import type { FAQItem } from "../../components/FAQ";
import { WEB_ASSET_DEMO_MEDIA } from "../../helpers/media-landing";

export type HomeCopy = {
  hero: {
    // The H1, a phrase at a time: the video chip and the shorts stack show
    // between these (see components/Hero.tsx).
    turnVideos: string;
    into: string;
    viral: string;
    shorts: string;
    subtitle: string;
  };
  reframe: { badge: string; title: string; body: string };
  stats: { title: string; body: string; madeWith: string };
  benefits: BenefitBlockData[];
  howTo: HowToBlockData;
  features: FeaturesBlockData;
  grow: { title: string; body: string; longVideoAlt: string; shortsAlt: string };
  testimonialsTitle: string;
  // Leaves the FAQ's default questions in place when absent.
  faq?: FAQItem[];
};

export const home: HomeCopy = {
  hero: {
    turnVideos: "Turn videos",
    into: "into",
    viral: "viral",
    shorts: "shorts",
    subtitle:
      "Create TikToks, Reels, Shorts from your long videos in just one click.",
  },
  reframe: {
    badge: "New",
    title: "Introducing **AI Reframe 2**",
    body: "Resize your videos for any platform with our most advanced AI yet. It analyzes your scenes and applies the perfect layout—Split Screen, Screencasts, Gaming, and more—turning long videos into pro-quality clips in just one click.",
  },
  stats: {
    title: "{clips} clips made by **{users} creators**",
    body: "Grow your audience like the top creators by sharing short-form content daily. Klap will help you create engaging video clips from your long videos in just a few clicks.",
    madeWith: "Made with",
  },
  benefits: [
    {
      title: "Never spend time and money on **video clipping** again",
      description:
        "Klap will turn all your videos into many viral vertical clips. Our AI-powered tool will create engaging video clips from your content in just a few clicks.",
      media: WEB_ASSET_DEMO_MEDIA.shorts,
      cta: "Create video clips",
    },
    {
      title: "**Automatically create** viral worthy content",
      description:
        "Don’t trust us, trust the algorithm. Klap will instantly turn each video clip into viral worthy content. Our AI-powered tool will create engaging video clips and score their potential to go viral. And yes, we provide AI generated captions to post alongside the videos too.",
      media: WEB_ASSET_DEMO_MEDIA.aiGenerate,
      cta: "Create video clips",
    },
    {
      title: "Grow your audience by **repurposing your videos**",
      description:
        "Don’t limit yourself to just one channel, when you can repurpose your content to fit them all. Expand the reach of your long form content with Klap’s one-click solution: turning Youtube videos into multiple Reels, TikToks, and Shorts.",
      media: WEB_ASSET_DEMO_MEDIA.schedule,
      cta: "Create video clips",
    },
    {
      title: "**Posting everyday** has never been easier",
      description:
        "Automate your video creation and posting process. Once you automatically turn your long form videos into engaging clips with Klap, you can instantly share your new videos across your social media platforms. One AI video editor tool that does it all!",
      media: WEB_ASSET_DEMO_MEDIA.publish,
      cta: "Create video clips",
    },
  ],
  howTo: {
    title: "Wondering **how Klap works?**",
    items: [
      {
        media: WEB_ASSET_DEMO_MEDIA.upload,
        title: "Start by uploading a video",
        description:
          "Simply paste a link to your Youtube video, or upload a video file, into Klap. Our rule of thumb: a one-minute long video produces about 5 video clips.",
      },
      {
        media: WEB_ASSET_DEMO_MEDIA.aiGenerate,
        title: "Let Klap’s AI magically create vertical videos for you",
        description:
          "Just sit back and relax, while Klap does all the work for you. In a matter of minutes, we will give you multiple viral-worthy clips from your original video that you can use for Reels, TikTok, or Shorts.",
      },
      {
        media: WEB_ASSET_DEMO_MEDIA.publish,
        title: "Post your videos and grow your followers",
        description:
          "After you make any final touches on your clipped videos, you’re all set to export them in high-resolution and share them on your other social channels. Get ready to see your followers and engagement grow!",
      },
    ],
  },
  features: featuresThatMakesKlapBetter,
  grow: {
    title: "Grow your audience **without any extra work**",
    body: "Save time and create more. Our AI video clip generator will turn all your long videos into a viral clips for your social media channels. Keep posting and watch your followers grow.",
    longVideoAlt: "Klap long video",
    shortsAlt: "Klap shorts",
  },
  testimonialsTitle: "What our users **are saying**",
};
